// Generated by tutorials/record.cjs from tutorials/articles/h42-changing-menu-items.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h42 --text-only` (wording) or
// `node tutorials/record.cjs h42` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'changing-menu-items',
  catalogId: 'h42',
  title: 'Changing items and rows',
  summary: 'Turn on editing, change an item or its size right in the grid, check what the change does to each diet, and add, move, copy and delete rows. Every change saves as you make it.',
  audience: 'Menu planners and dietary managers who keep the menu cycle up to date',
  area: 'menus',
  learn: [
    'Turn on editing, and why only one person edits a menu at a time',
    'Change an item and check what each diet gets after the change',
    'Change an item\'s portion size',
    'Put a row back to its item\'s AIM rules',
    'Add a row and move it',
    'Copy rows to another day or meal',
    'Delete a row, and finish editing',
  ],
  keywords: [
    'edit menu',
    'change item',
    'swap item',
    'replace item',
    'item picker',
    'portion size',
    'size',
    'add item',
    'add row',
    'move row',
    'move up',
    'delete row',
    'copy row',
    'super copy',
    'paste',
    'aim',
    'auto item mods',
    'fill item',
    'lock',
    'done',
    'save',
    'menu cycle',
  ],
  steps: [
    {
      id: 'edit',
      part: 'Start editing',
      heading: 'Select Edit menu',
      text: 'The menu opens read only. Select Edit menu to start changing it. Editing shows beside the name, and Add Item and Done appear.\n\nWhile you edit, the menu is yours alone: anyone else who opens it sees it read only. Each change saves as you make it, so there is no Save button.',
      media: {
        image: {
          src: '/help/changing-menu-items/edit.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/changing-menu-items/edit.mp4',
          width: 1280,
          height: 842,
          durationSec: 4,
        },
        alt: 'Fall/Winter Cycle 2026 on Day 22 Afternoon with Editing beside the name, and the Done and Add Item buttons.',
      },
    },
    {
      id: 'pick-item',
      part: 'Change an item',
      heading: 'Change the item',
      text: 'Click the item\'s name (or select the row and press Enter). Type part of the new item\'s name and pick it.\n\nSize changes to the new item\'s usual size. Pick another under Size if you need to.',
      media: {
        image: {
          src: '/help/changing-menu-items/pick-item.webp',
          width: 1280,
          height: 656,
        },
        clip: {
          src: '/help/changing-menu-items/pick-item.mp4',
          width: 1280,
          height: 656,
          durationSec: 6,
        },
        alt: 'The item picker over the dessert row, with PIE APPLE picked in place of BANANA PUDDING and Size 1 slice.',
      },
    },
    {
      id: 'swap-check',
      part: 'Change an item',
      heading: 'Check what each diet gets',
      text: 'Select Save. When the change would serve a diet differently from now, a check lists each one: what it gets now, and what it gets after the change.\n\nWith Set the diets from AIM rules checked, the new item\'s Auto Item Mods set the diets. A diet that is not served now and would be served after the change starts with Keep now checked, so nothing new lands on a tray by surprise. Here Renal stays without dessert.',
      media: {
        image: {
          src: '/help/changing-menu-items/swap-check.webp',
          width: 1280,
          height: 678,
        },
        clip: {
          src: '/help/changing-menu-items/swap-check.mp4',
          width: 1280,
          height: 678,
          durationSec: 2.5,
        },
        alt: 'Change BANANA PUDDING to PIE APPLE: Cardiac gets FRUIT COCKTAIL, CCHO gets PIE APPLE SF, Renal is not served now with Keep now checked, and Minced and Moist and Pureed get PIE APPLE made to that texture.',
      },
    },
    {
      id: 'swap-done',
      part: 'Change an item',
      heading: 'Change the item',
      text: 'Clear Keep now for any diet that should follow the new item, then select Change item. The row changes at once and is saved.',
      media: {
        image: {
          src: '/help/changing-menu-items/swap-done.webp',
          width: 1280,
          height: 1014,
        },
        clip: {
          src: '/help/changing-menu-items/swap-done.mp4',
          width: 1280,
          height: 1014,
          durationSec: 5.8,
        },
        alt: 'The DESSERT row now PIE APPLE 1 slice, with PIE APPLE SF for CCHO, FRUIT COCKTAIL for Cardiac and Renal still not served.',
      },
    },
    {
      id: 'size',
      part: 'Change an item',
      heading: 'Change a portion size',
      text: 'To change only the size, open the picker the same way and pick the size under Size, then select Save. The diets keep their settings, because the item has not changed.',
      media: {
        image: {
          src: '/help/changing-menu-items/size.webp',
          width: 1280,
          height: 656,
        },
        clip: {
          src: '/help/changing-menu-items/size.mp4',
          width: 1280,
          height: 656,
          durationSec: 6,
        },
        alt: 'SOUP CHICKEN NOODLE now 4 fl oz, with the Cardiac and Low Sodium 2g substitutes unchanged.',
      },
    },
    {
      id: 'aim-fill',
      part: 'Change an item',
      heading: 'Put a row back to its AIM rules',
      text: 'Select the row, then Rules > AIM - Fill Item on MC. It sets every diet from the item\'s Auto Item Mods again, which is useful after a row has been changed by hand.\n\nFirst it lists each diet that would change. Select Fill from AIM to go ahead, or Cancel to keep the row as it is. Here Cancel keeps Renal without dessert.',
      media: {
        image: {
          src: '/help/changing-menu-items/aim-fill.webp',
          width: 1280,
          height: 378,
        },
        alt: 'AIM - Fill Item on MC for PIE APPLE: Renal is not served now and would be served PIE APPLE after the fill.',
      },
    },
    {
      id: 'add',
      part: 'Add, move and copy rows',
      heading: 'Add a row',
      text: 'Select the row the new one should follow, then Add Item. Type part of the item\'s name and pick it. Where says where it goes; then select Add.\n\nThe new row starts with each diet\'s usual setting, and the item\'s AIM rules if it has any.',
      media: {
        image: {
          src: '/help/changing-menu-items/add.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/changing-menu-items/add.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'CORN added under VEGETABLE as the 2nd choice, below MIXED VEGETABLES, with the textures from its AIM rules.',
      },
    },
    {
      id: 'move',
      part: 'Add, move and copy rows',
      heading: 'Move a row',
      text: 'Select the row, then Row > Move up or Move down. The rows under it move with it.\n\nHere CORN becomes the 1st choice. Move out one level and Move in one level change whether a row stands on its own or goes with the row above.',
      media: {
        image: {
          src: '/help/changing-menu-items/move.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/changing-menu-items/move.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.9,
        },
        alt: 'CORN moved up to be the 1st vegetable choice, above MIXED VEGETABLES.',
      },
    },
    {
      id: 'copy',
      part: 'Add, move and copy rows',
      heading: 'Copy rows',
      text: 'Select a row, then open Copy. Copy row copies the row alone; Super Copy copies it with the rows under it, such as a salad with its dressing.\n\nThe row keeps every diet setting when you paste it.',
      media: {
        image: {
          src: '/help/changing-menu-items/copy.webp',
          width: 1280,
          height: 728,
        },
        clip: {
          src: '/help/changing-menu-items/copy.mp4',
          width: 1280,
          height: 728,
          durationSec: 4.7,
        },
        alt: 'The Copy menu open on CHEF SALAD with Copy row, Super Copy - with children, Cut and Paste.',
      },
    },
    {
      id: 'paste',
      part: 'Add, move and copy rows',
      heading: 'Paste them into another meal',
      text: 'Go to the day and meal you want, select the row the copy should follow, then Copy > Paste Below. The rows are added below it. To put the copy into a row instead, replacing it, select that row and choose Copy > Paste.\n\nHere the chef salad and its dressing become the dinner\'s 3rd entree choice.',
      media: {
        image: {
          src: '/help/changing-menu-items/paste.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/changing-menu-items/paste.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'Day 22 Evening with CHEF SALAD and DRESSING RANCH PC pasted under ENTREE as the 3rd choice, below SANDWICH TURKEY.',
      },
    },
    {
      id: 'delete',
      part: 'Add, move and copy rows',
      heading: 'Delete a row',
      text: 'Select the row and press Del (or select Row > Delete row). The question names what goes with it: the rows under it and its diet settings. Select Delete.\n\nHere the turkey sandwich comes off the dinner menu, leaving the chef salad as the alternate.',
      media: {
        image: {
          src: '/help/changing-menu-items/delete.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/changing-menu-items/delete.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'The dinner ENTREE category after the delete: BEEF STEW and its sides, then CHEF SALAD with its dressing as the 2nd choice.',
      },
    },
    {
      id: 'history',
      part: 'Check and finish your changes',
      heading: 'Check your changes',
      text: 'Select the changes count at the bottom right. Save history lists every change you made since you opened the menu, newest first. A change that did not save says so here.',
      media: {
        image: {
          src: '/help/changing-menu-items/history.webp',
          width: 944,
          height: 478,
        },
        clip: {
          src: '/help/changing-menu-items/history.mp4',
          width: 944,
          height: 478,
          durationSec: 2,
        },
        alt: 'Save history with the 7 changes of this session, newest first: the delete, the paste, CORN moved and added, the soup size and the dessert changed to PIE APPLE.',
      },
    },
    {
      id: 'done',
      part: 'Check and finish your changes',
      heading: 'Select Done',
      text: 'When you have finished, select Done. The menu goes back to read only and others can edit it again. Closing the page does the same.',
      media: {
        image: {
          src: '/help/changing-menu-items/done.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/changing-menu-items/done.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.6,
        },
        alt: 'The menu read only again, with Edit menu beside the name and All changes saved at the bottom.',
      },
    },
  ],
  tips: [
    'Click an item\'s name to change its item or size, the same as selecting the row and pressing Enter. To select a row without changing it, click beside the name or use the arrow keys.',
    'Ctrl+Up and Ctrl+Down move the selected row; Ctrl+Left and Ctrl+Right move it out of or under the row above.',
    'Ctrl+C copies the selected row and Ctrl+Shift+C copies it with the rows under it. Ctrl+V pastes into the selected row, replacing it; Ctrl+Shift+V pastes below it.',
    'Rules > Use AIM Attributes says whether a new item\'s AIM rules are applied when you change an item. Leave it on unless you plan to set every diet yourself.',
  ],
  notes: [
    'There is no Save button: each change is saved the moment you make it. The bottom left of the page says when everything is saved.',
    'While you are editing, the menu is locked: anyone else who opens it sees it read only until you select Done or close the page.',
    'Deleting a row also deletes the rows under it and its diet settings. It cannot be undone.',
    'Paste puts the copy into the selected row and replaces what was there. A Copy keeps the rows under that row; a Super Copy replaces them too, and asks first. A synced row cannot be replaced: break its sync first, or use Paste Below.',
    'Changing menus needs the right role. If Edit menu is missing or greyed, ask your administrator.',
    'A row that is synced with rows on other days asks first whether to change the whole group (see Synced rows and Push To).',
  ],
  related: [
    'reading-a-menu-cycle',
    'diet-and-consistency-rules',
    'synced-rows-and-push-to',
    'auto-item-mods',
    'menu-swap-and-requests',
  ],
  recorded: '2026-10-26',
};

export default article;
